import React from "react";
import {
  Box,
  Typography,
  Stack,
  Paper,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Link,
  Card,
  CardContent,
  Button,
  useTheme,
  alpha,
} from "@mui/material";
import {
  Lightbulb,
  Assignment,
  Speed,
  WorkspacePremium,
  AutoStories,
  Videocam,
  Architecture,
  ChevronRight,
} from "@mui/icons-material";

const GOLD = "#D4AF37";

export default function CourseStructure() {
  const theme = useTheme();

  const tips = [
    {
      title: "Start with your goals",
      desc: "Setting goals for what learners will accomplish in your course (learning objectives) helps you determine what content to include and how to teach it effectively.",
      icon: <Architecture />,
    },
    {
      title: "Create an outline",
      desc: "Decide what skills you'll teach and how. Group related lectures into sections. Each section should have at least 3 lectures and include one assignment or activity.",
      icon: <Assignment />,
    },
    {
      title: "Introduce yourself & create momentum",
      desc: "People want to start learning quickly. Make an introduction section that gives learners something to be excited about in the first 10 minutes.",
      icon: <Speed />,
    },
    {
      title: "Sections have a clear objective",
      desc: "Introduce each section by describing its goal and why it’s important. Give lectures and sections titles that reflect their content and have a logical flow.",
      icon: <AutoStories />,
    },
    {
      title: "Lectures cover one concept",
      desc: "A good lecture length is 2-7 minutes. Cover a single topic per lecture so learners can easily find and re-watch them later.",
      icon: <Lightbulb />,
    },
  ];

  const requirements = [
    "Your course must have at least five lectures",
    "All lectures must add up to at least 30+ minutes of total video",
    "Valuable educational content, free of promotional or distracting materials",
    "Adherence to all course quality requirements guidelines",
  ];

  const resources = [
    { title: "KaaS Trust & Safety", desc: "Our policies for instructors and students" },
    { title: "Join the instructor community", desc: "A place to connect with other instructors" },
    { title: "Official KaaS Course", desc: "Learn about course creation from the KaaS Team" },
  ];

  return (
    <Box sx={{ maxWidth: 1000, mx: "auto" }}>
      <Typography variant="h4" fontWeight={900} gutterBottom>
        Course Structure
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 6 }}>
        Planning your course carefully will create a clear learning path for students and help you once you film.
      </Typography>

      <Grid container spacing={6}>
        <Grid item xs={12} md={8}>
          <Box sx={{ mb: 6 }}>
            <Typography variant="h5" fontWeight={800} sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1.5 }}>
              <WorkspacePremium sx={{ color: GOLD }} />
              Tips for a successful structure
            </Typography>
            <Stack spacing={4}>
              {tips.map((tip, idx) => (
                <Box key={idx} sx={{ display: "flex", gap: 3 }}>
                  <Box 
                    sx={{ 
                      flexShrink: 0, 
                      width: 48, 
                      height: 48, 
                      borderRadius: "12px", 
                      bgcolor: alpha(GOLD, 0.1), 
                      color: GOLD, 
                      display: "flex", 
                      alignItems: "center", 
                      justifyContent: "center" 
                    }}
                  >
                    {tip.icon}
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                      {tip.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {tip.desc}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Stack>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography variant="h6" fontWeight={800} gutterBottom>
              Basic Requirements
            </Typography>
            <List sx={{ bgcolor: alpha(GOLD, 0.02), borderRadius: 4, p: 2 }}>
              {requirements.map((req, idx) => (
                <ListItem key={idx}>
                  <ListItemIcon sx={{ minWidth: 32, color: GOLD }}>
                    <ChevronRight fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={req} primaryTypographyProps={{ variant: "body2", fontWeight: 500 }} />
                </ListItem>
              ))}
            </List>
          </Box>

          <Box>
            <Typography variant="h6" fontWeight={800} gutterBottom>
              Internal Resources
            </Typography>
            <Grid container spacing={3}>
              {resources.map((res, idx) => (
                <Grid item xs={12} sm={6} key={idx}>
                  <Link href="#" underline="none">
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        p: 2, 
                        borderRadius: 3, 
                        transition: "0.2s", 
                        "&:hover": { borderColor: GOLD, bgcolor: alpha(GOLD, 0.05) } 
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={800} color="primary" gutterBottom>
                        {res.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {res.desc}
                      </Typography>
                    </Paper>
                  </Link>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card 
            sx={{ 
              borderRadius: 6, 
              textAlign: "center", 
              p: 2, 
              border: "1px solid", 
              borderColor: "divider", 
              boxShadow: "0 10px 40px rgba(0,0,0,0.05)",
              position: "sticky",
              top: 20
            }}
          >
            <CardContent>
              <Box sx={{ width: 120, height: 120, mx: "auto", mb: 2, bgcolor: alpha(GOLD, 0.1), borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <AutoStories sx={{ fontSize: 60, color: GOLD }} />
              </Box>
              <Typography variant="h6" fontWeight={800} gutterBottom>
                Resource Library
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                Tips and guides to structuring a course your students will love.
              </Typography>
              <Button 
                variant="outlined" 
                fullWidth 
                sx={{ 
                  borderRadius: "50px", 
                  borderColor: GOLD, 
                  color: GOLD, 
                  fontWeight: 700,
                  "&:hover": { borderColor: GOLD, bgcolor: alpha(GOLD, 0.05) }
                }}
              >
                Teaching Center
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
